import Box from '@mui/material/Box';
import { lighten, SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  ApprovalAction,
  getV1CompaniesCompanyIdLoadsLoadIdLoadApprovals,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { ApprovalAssignment } from '~hooks/useApprovalsAssignments/models';
import { Load } from '~hooks/useLoad/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getApprovalStatus } from '~utils/jobs/job-utils';

import { FlexColumn, FlexRow } from '../styled';

interface LoadApprovalsProps {
  companyId: string;
  load: Load;
  isOrderEditable: boolean;
  sx?: SxProps<Theme>;
}

export default function LoadApprovals({
  companyId,
  load: initialLoad,
  isOrderEditable,
  sx,
}: LoadApprovalsProps) {
  const isInitialLoad = useRef(true);
  const { userStore } = useStores();
  const userPermissions = userStore.getPermissions();

  const [load, setLoad] = useState(initialLoad);
  const [approvalsState, setApprovalsState] = useState<{
    approvals: ApprovalAssignment[];
    isLoading: boolean;
  }>({ approvals: [], isLoading: true });

  const shouldShowSpinner = approvalsState.isLoading && isInitialLoad.current;

  const { isApproved: isLoadApproved } = getApprovalStatus(
    load,
    userPermissions,
    isOrderEditable,
  );

  const fetchLoadApprovals = useCallback(async () => {
    setApprovalsState((state) => ({ ...state, isLoading: true }));

    try {
      const response = await getV1CompaniesCompanyIdLoadsLoadIdLoadApprovals({
        path: { 'company-id': companyId, 'load-id': load.id },
      });
      const approvals = response.data.data
        .map((approval) => ApprovalAssignment.parse(approval))
        .filter((approval) => approval.approvalAction === ApprovalAction.APPROVE);

      setApprovalsState({ isLoading: false, approvals });
    } catch (error) {
      // A 404 here means the load was deleted out from under an open drawer
      // (e.g. removed in another tab before the RTU pruned the local cache).
      // That is expected, not an actionable error. See HORIZON-FE-2MM.
      try {
        connection.handleRequestError(
          error,
          t('error_messages.jobs.failed_to_fetch_load_approvals'),
          [404],
        );
      } catch {
        // handleRequestError always rethrows (returns never); swallow it so this
        // fire-and-forget effect does not raise an unhandled rejection.
        // The toast/Sentry report already fired for non-suppressed statuses.
      }
      setApprovalsState((state) => ({ ...state, isLoading: false }));
    } finally {
      isInitialLoad.current = false;
    }
  }, [companyId, load.id, isLoadApproved]);

  useEffect(() => {
    fetchLoadApprovals();
  }, [fetchLoadApprovals]);

  useEffect(() => {
    setLoad(initialLoad);
  }, [initialLoad]);

  if (!isLoadApproved) {
    return null;
  }

  if (!shouldShowSpinner && !approvalsState.approvals.length) {
    return null;
  }

  return (
    <Box sx={sx}>
      <FlexColumn
        sx={(theme) => ({
          bgcolor: lighten(theme.palette.success.main, 0.96),
          border: `solid 1px ${theme.palette.success.main}`,
          borderRadius: 2,
          gap: 1,
          p: theme.spacing(0.5, 1.5),
        })}
      >
        {shouldShowSpinner && (
          <FlexRow sx={{ py: 0.5, alignItems: 'center', gap: 1 }}>
            <Box>
              <LoadingSpinner
                isVisible
                loadingIndicatorColor="success"
                loadingIndicatorSize={16}
              />
            </Box>

            <Typography sx={{ fontSize: '12px', color: 'success.main', fontWeight: 600 }}>
              {t('ticket_drawer_v2.details.loading_approvals')}
            </Typography>
          </FlexRow>
        )}

        {Boolean(approvalsState.approvals.length) && (
          <>
            <Typography sx={{ color: 'success.main', fontSize: '16px', fontWeight: 600 }}>
              {t('ticket_drawer_v2.details.load_approved')}
            </Typography>

            <FlexColumn sx={{ gap: 0.25 }}>
              {approvalsState.approvals.map((approval) => (
                <ApprovalInfo key={approval.id} approval={approval} />
              ))}
            </FlexColumn>
          </>
        )}
      </FlexColumn>
    </Box>
  );
}

interface ApprovalInfoProps {
  approval: ApprovalAssignment;
}

function ApprovalInfo({ approval }: ApprovalInfoProps) {
  const { externalApproverName, approvalType, createdAt } = approval;
  const date = dayjs.tz(createdAt);

  return (
    <Typography sx={{ fontSize: '12px', color: 'success.main' }}>
      {externalApproverName && (
        <Typography component="span" sx={{ fontSize: 'inherit' }}>
          {`${t('common.by')} ${externalApproverName}${approvalType ? ` (${t(`approvals.driver_day.${approvalType}`)})` : ''} ${t('common.at').toLowerCase()} `}
        </Typography>
      )}

      <Typography
        component="span"
        sx={{ fontSize: 'inherit' }}
      >{`${date.format('hh:mm A')} ${date.format('MMM-DD-YYYY')}`}</Typography>
    </Typography>
  );
}
